import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { dispatchInboxSidebarTabChangeClear } from "../../utils/inboxTabSwitch";
import { fetchSubscriptionFeatures } from "../../redux/Slice/Subscription/subscriptionSlice";
import { fetchWorkspaces } from "../../redux/Slice/Workspace/workspaceSlice";
import TopNav from "../../components/Navbar/TopNav";
import LeftNav from "../../components/Navbar/LeftNav";
import BottomNav from "../../components/Navbar/BottomNav";
import TrialBanner from "../../components/TrialBanner/TrialBanner";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Outlet } from "react-router-dom";

const Home = React.memo(() => {
  const location = useLocation(); // Get the current route
  const dispatch = useDispatch();
  const prevPathRef = useRef(null);
  const outerBoxRef = useRef(null);
  const bannerWrapperRef = useRef(null);

  // Fetch once per account session so LeftNav/BottomNav can gate the AI item
  // on the plan's aiAssistants.enabled flag. Also fetch workspaces so
  // currentWorkspace is available to all features (dashboard counts, etc.)
  // without requiring the user to open the workspace switcher first.
  useEffect(() => {
    dispatch(fetchSubscriptionFeatures());
    dispatch(fetchWorkspaces());
  }, [dispatch]);

  // Leaving /home/.../inbox/... for another section: clear open chat from Redux so returning to Inbox does not restore URL/thread
  useEffect(() => {
    const curr = location.pathname;
    const prev = prevPathRef.current;
    prevPathRef.current = curr;
    if (prev == null) return;
    const wasInbox = prev.includes("/inbox/");
    const nowInbox = curr.includes("/inbox/");
    if (wasInbox && !nowInbox) {
      dispatchInboxSidebarTabChangeClear(dispatch);
    }
  }, [location.pathname, dispatch]);

  // Keep --trial-banner-height CSS var in sync so Inbox drawer offsets correctly
  useLayoutEffect(() => {
    const el = bannerWrapperRef.current;
    const box = outerBoxRef.current;
    if (!el || !box) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight ?? 0;
      box.style.setProperty("--trial-banner-height", `${h}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isDashboardRoute = location.pathname === "/" || location.pathname === "/dashboard";

  // Route-level opt-out for TopNav (e.g. Onboard, Reports, Inbox). `useMatches`/
  // route `handle` needs a data router (createBrowserRouter+RouterProvider);
  // this app still uses classic BrowserRouter, so a pathname list is the
  // compatible option. `includes` (not `endsWith`) -- Inbox routes always carry
  // trailing segments (/inbox/:userid, /inbox/:userid/chat/:contactid) so they
  // never literally end with "/inbox"; matches the same `.includes("/inbox/")`
  // check already used elsewhere in this file (see the chat-clear effect above).
  // Settings added 2026-09-10: rebuilt with its own two-piece PanelHeading
  // (narrow rail header + full content header), matching Inbox/AI Settings —
  // see SettingsOption.jsx/Settings.jsx. The legacy global bar's Settings
  // branch (TopNav.jsx) is left in place for now (harmless dead code) rather
  // than removed, in case another still-unmigrated page needs it as reference.
  // Broadcasts added 2026-09-10: same two-piece PanelHeading rebuild as
  // Settings/Reports (BroadcastSidebar.jsx/Broadcasts.jsx). Only the list
  // page ("/contact-groups") got this treatment -- "/contact-groups/new"
  // (CreateBroadcast.jsx) wasn't touched and still relies on the global
  // TopNav for its own header, so it's explicitly carved back out below
  // (a plain substring match on "/contact-groups" would otherwise also
  // match it and leave it with no header at all).
  // Calls added 2026-09-10: same rebuild, and unlike "/contact-groups" it has
  // no nested sub-route to carve back out -- "/calls" is the only route under it.
  // "/chatbot-widget" — Create Website Chat Widget wizard now carries its own
  // PanelHeading (same mechanism /ai and /settings already use here), so the
  // global TopNav's stale "Settings › Integrations" breadcrumb no longer
  // shows above it (Rahim, 2026-09-23).
  const NO_TOP_NAV_ROUTES = ["/onboard", "/reports", "/inbox/", "/ai", "/settings", "/contact-groups", "/calls", "/chatbot-widget"];
  const showTopNav = location.pathname.includes("/contact-groups/new")
    ? true
    : !NO_TOP_NAV_ROUTES.some((path) => location.pathname.includes(path));

  return (
    <Box
      ref={outerBoxRef}
      sx={{
        width: "100%",
        "--top-nav-height": { xs: "40px", sm: "48px", md: "50px", lg: "50px", xl: "50px" },
        "--mobile-bottom-nav-height": { xs: "56px", sm: "62px", md: "0px", lg: "0px", xl: "0px" },
        "--trial-banner-height": "0px",
        height: { xs: "100dvh", sm: "100dvh", md: "100vh", lg: "100vh", xl: "100vh" },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Trial banner spans full width above everything (left nav + top bar) */}
      <Box ref={bannerWrapperRef}>
        <TrialBanner />
      </Box>
      <Grid
        container
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* First Column: Left Navigation - Hidden on xs/sm, shown on md+ */}
        <Grid
          size={{
            xs: 0,
            sm: 0,
            md: 0.5,
            lg: 0.5,
            xl: 0.5,
          }}
          sx={{
            overflow: { xs: "auto", sm: "auto", md: "hidden", lg: "hidden", xl: "hidden" }, // Prevent horizontal scrollbar on md+
            overflowX: "hidden", // Prevent horizontal scrollbar
            height: "100%",
            display: { xs: "none", sm: "none", md: "block", lg: "block", xl: "block" },
          }}
        >
          <LeftNav />
        </Grid>

        {/* Second Column: Main Content */}
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 11.5,
            lg: 11.5,
            xl: 11.5,
          }}
          sx={{
            height: {
              xs: "100%",
              sm: "100%",
              md: "100%",
              lg: "100%",
              xl: "100%",
            },
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: isDashboardRoute 
              ? { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" } // Keep hidden for all
              : "hidden",
          }}
        >
          {/* xs/sm: TopNav + scrollable outlet + in-flow BottomNav. md+: TopNav + outlet only (BottomNav hidden). */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              flex: 1,
            }}
          >
            {showTopNav && (
              <Box sx={{ flexShrink: 0 }}>
                <TopNav />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                flexShrink: 1,
                flex: 1,
                overflowY: isDashboardRoute 
                  ? { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" } // Dashboard handles its own scrolling
                  : "hidden",
                overflowX: "hidden",
                minHeight: 0,
                boxSizing: "border-box",
                backgroundColor: (theme) => theme.palette.background.default,
              }}
            >
              <Outlet />
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                display: { xs: "block", sm: "block", md: "none", lg: "none", xl: "none" },
              }}
            >
              <BottomNav />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

export default Home;